import { useEffect, useState, useRef } from "react";
import { getUserIdFromToken, getUserIdFromStorage } from "./utils/auth";
import { useNavigate } from "react-router-dom";
import styles from './SessionCreation.module.css';

export default function CreateSessionPage() {
  const [sets, setSets] = useState([]);
  const [groups, setGroups] = useState([]);

  const [selectedSets, setSelectedSets] = useState([
    { id: Date.now(), selectedSetId: "", rarities: [], packsOpened: 1 }
  ]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [datePulled, setDatePulled] = useState(new Date().toISOString().split('T')[0]);

  const [newGroupName, setNewGroupName] = useState("");
  
  const defaultGroupCreatedRef = useRef(false);

  const navigate = useNavigate();

  /* -------------------- Load sets & groups -------------------- */
  useEffect(() => {
    let userId = getUserIdFromToken();
    
    if (!userId) {
      userId = getUserIdFromStorage();
    }

    fetch(`${process.env.REACT_APP_API_URL}/api/sets`)
      .then(res => res.json())
      .then(setSets);

    if (userId) {
      fetch(`${process.env.REACT_APP_API_URL}/api/groups?userId=${userId}`)
        .then(res => res.json())
        .then(async data => {
          // If no groups exist, create Default group (only once)
          if (data.length === 0 && !defaultGroupCreatedRef.current) {
            defaultGroupCreatedRef.current = true;
            try {
              const res = await fetch(`${process.env.REACT_APP_API_URL}/api/groups`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ 
                  name: "Default",
                  userId 
                })
              });
              
              if (res.ok) {
                const newGroup = await res.json();
                setGroups([newGroup]);
                setSelectedGroupId(newGroup._id);
              } else if (res.status === 409) {
                // Group already exists (race condition), fetch again
                const retryRes = await fetch(`${process.env.REACT_APP_API_URL}/api/groups?userId=${userId}`);
                const retryData = await retryRes.json();
                const sortedGroups = retryData.sort((a, b) => {
                  if (a.name === 'Default') return -1;
                  if (b.name === 'Default') return 1;
                  return a.name.localeCompare(b.name);
                });
                setGroups(sortedGroups);
                const defaultGroup = sortedGroups.find(g => g.name === 'Default');
                if (defaultGroup) {
                  setSelectedGroupId(defaultGroup._id);
                } else if (sortedGroups.length > 0) {
                  setSelectedGroupId(sortedGroups[0]._id);
                }
              }
            } catch (error) {
              console.error('Error creating default group:', error);
            }
          } else {
            // Sort groups with Default first
            const sortedGroups = data.sort((a, b) => {
              if (a.name === 'Default') return -1;
              if (b.name === 'Default') return 1;
              return a.name.localeCompare(b.name);
            });
            setGroups(sortedGroups);
            // Set default group as selected
            const defaultGroup = sortedGroups.find(g => g.name === 'Default');
            if (defaultGroup) {
              setSelectedGroupId(defaultGroup._id);
            } else if (sortedGroups.length > 0) {
              setSelectedGroupId(sortedGroups[0]._id);
            }
          }
        });
    }
  }, []);

  /* -------------------- Load rarities when set changes -------------------- */
  const handleSetChange = async (setEntryId, newSetId) => {
    // Check for duplicate set selection
    const isDuplicate = selectedSets.some(entry => 
      entry.id !== setEntryId && entry.selectedSetId === newSetId
    );
    
    if (isDuplicate && newSetId) {
      alert("This set has already been selected. Please choose a different set.");
      return;
    }

    setSelectedSets(prev => 
      prev.map(entry => {
        if (entry.id !== setEntryId) return entry;
        
        return {
          ...entry,
          selectedSetId: newSetId,
          rarities: []
        };
      })
    );

    if (!newSetId) return;

    // Fetch the set's rarities
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/sets/${newSetId}`);
    const set = await res.json();
    
    setSelectedSets(prev => 
      prev.map(entry => {
        if (entry.id !== setEntryId) return entry;
        
        return {
          ...entry,
          rarities: set.rarities.map(r => ({
            rarity: r.name,
            count: 0
          }))
        };
      })
    );
  };

  const addAnotherSet = () => {
    setSelectedSets(prev => [...prev, { id: Date.now(), selectedSetId: "", rarities: [], packsOpened: 1 }]);
  };

  const removeSet = (setEntryId) => {
    setSelectedSets(prev => prev.filter(entry => entry.id !== setEntryId));
  };

  const updateRarityCount = (setEntryId, rarityIndex, value) => {
    setSelectedSets(prev => 
      prev.map(entry => {
        if (entry.id !== setEntryId) return entry;
        
        const newRarities = [...entry.rarities];
        // Allow empty string, otherwise convert to number
        newRarities[rarityIndex].count = value === '' ? '' : Number(value);
        
        return {
          ...entry,
          rarities: newRarities
        };
      })
    );
  };

  const updatePacksOpened = (setEntryId, value) => {
    setSelectedSets(prev => 
      prev.map(entry => {
        if (entry.id !== setEntryId) return entry;
        // Allow empty string, otherwise convert to number
        return { ...entry, packsOpened: value === '' ? '' : Number(value) };
      })
    );
  };

  /* -------------------- Group creation -------------------- */
  const createGroup = async () => {
    if (!newGroupName.trim()) return;

    let userId = getUserIdFromToken();
    
    if (!userId) {
      userId = getUserIdFromStorage();
    }

    if (!userId) {
      alert("Unable to identify user. Please log in again.");
      return;
    }

    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/groups`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ 
        name: newGroupName,
        userId 
      })
    });

    if (!res.ok) {
      const errorData = await res.json();
      alert(`Failed to create group: ${errorData.error || 'Unknown error'}`);
      return;
    }

    const group = await res.json();
    setGroups(prev => [...prev, group]);
    setSelectedGroupId(group._id);
    setNewGroupName("");
    alert(`Group "${group.name}" created!`);
  };

  /* -------------------- Session creation -------------------- */
  const createSession = async () => {
    // Try both methods to get userId
    let userId = getUserIdFromToken();
    
    if (!userId) {
      console.log('Could not get userId from token, trying storage...');
      userId = getUserIdFromStorage();
    }
    
    console.log('Final userId for session creation:', userId);

    if (!userId) {
      alert("Unable to identify user. Please log in again.");
      return;
    }

    const groupId = selectedGroupId;

    // Create a session for each selected set
    try {
      const sessionPromises = selectedSets.map(setEntry => {
        const requestBody = {
          setId: setEntry.selectedSetId,
          groupId,
          userId,
          packsOpened: setEntry.packsOpened === '' ? 1 : setEntry.packsOpened,
          cardsByRarity: setEntry.rarities.map(r => ({
            rarity: r.rarity,
            count: r.count === '' ? 0 : r.count
          })),
          datePulled
        };
        
        console.log('Sending session creation request:', requestBody);

        return fetch(`${process.env.REACT_APP_API_URL}/api/sessions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody)
        }).then(response => ({ response, setEntry }));
      });

      const results = await Promise.all(sessionPromises);

      // Separate successful and failed results
      const successfulSets = [];
      const failedSets = [];

      for (const { response, setEntry } of results) {
        if (response.ok) {
          successfulSets.push(setEntry);
        } else {
          const errorData = await response.json();
          const setName = sets.find(s => s._id === setEntry.selectedSetId)?.name || 'Unknown Set';
          failedSets.push({ setEntry, setName, error: errorData.error || 'Unknown error' });
        }
      }

      // Remove successful sets from the form
      if (successfulSets.length > 0) {
        setSelectedSets(prev => 
          prev.filter(entry => !successfulSets.some(s => s.id === entry.id))
        );
      }

      // Handle results
      if (failedSets.length > 0 && successfulSets.length > 0) {
        // Partial success
        const failureMessages = failedSets.map(f => `• ${f.setName}: ${f.error}`).join('\n');
        alert(`Created ${successfulSets.length} session(s) successfully.\n\nFailed to create ${failedSets.length} session(s):\n${failureMessages}\n\nThe failed sets remain on the form. Please fix the issues and try again.`);
      } else if (failedSets.length > 0) {
        // All failed
        const failureMessages = failedSets.map(f => `• ${f.setName}: ${f.error}`).join('\n');
        console.error('Session creation failed:', failedSets);
        alert(`Failed to create all sessions:\n${failureMessages}\n\nPlease fix the issues and try again.`);
      } else {
        // All successful
        console.log('Sessions created successfully:', successfulSets);
        alert(`${successfulSets.length} session${successfulSets.length > 1 ? 's' : ''} created!`);
        navigate("/dashboard");
      }
    } catch (error) {
      console.error('Error creating sessions:', error);
      alert('Failed to create sessions. Please try again.');
    }
  };

  /* -------------------- Render -------------------- */
  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>Create Opening Session</h1>
        <button
          onClick={() => navigate("/dashboard")}
          className={styles.backButton}
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Group selection */}
      <div className={styles.formSection}>
        <label className={styles.label}>Group</label>
        <select
          className={styles.select}
          value={selectedGroupId}
          onChange={e => setSelectedGroupId(e.target.value)}
        >
          {groups.map(group => (
            <option key={group._id} value={group._id}>
              {group.name}
            </option>
          ))}
        </select>

        <div className={styles.groupCreationRow}>
          <input
            className={styles.groupInput}
            placeholder="New group name"
            value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
          />
          <button
            onClick={createGroup}
            className={styles.createGroupButton}
          >
            Create Group
          </button>
        </div>
      </div>

      {/* Date Pulled */}
      <div className={styles.formSection}>
        <label className={styles.label}>Date Pulled</label>
        <input
          type="date"
          className={styles.input}
          value={datePulled}
          onChange={e => setDatePulled(e.target.value)}
        />
      </div>

      {/* Set selection */}
      {selectedSets.map((setEntry, setIndex) => (
        <div key={setEntry.id} className={styles.formSection}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <label className={styles.label}>
              Pokémon Set {selectedSets.length > 1 ? `#${setIndex + 1}` : ''}
            </label>
            {selectedSets.length > 1 && (
              <button
                onClick={() => removeSet(setEntry.id)}
                className={styles.backButton}
                style={{ 
                  backgroundColor: '#ef4444', 
                  padding: '0.25rem 0.75rem',
                  fontSize: '0.875rem'
                }}
              >
                Remove
              </button>
            )}
          </div>
          <select
            className={styles.select}
            value={setEntry.selectedSetId}
            onChange={e => handleSetChange(setEntry.id, e.target.value)}
          >
            <option value="">Select a set</option>
            {sets.map(set => (
              <option key={set._id} value={set._id}>
                {set.name}
              </option>
            ))}
          </select>

          {/* Packs opened for this set */}
          <div style={{ marginTop: '1rem' }}>
            <label className={styles.label}>Packs Opened</label>
            <input
              type="number"
              min={1}
              className={styles.numberInput}
              value={setEntry.packsOpened}
              onChange={e => updatePacksOpened(setEntry.id, e.target.value)}
            />
          </div>

          {/* Rarity table for this set */}
          {setEntry.rarities.length > 0 && (
            <div className={styles.tableContainer} style={{ marginTop: '1rem' }}>
              <table className={styles.table}>
                <thead className={styles.tableHead}>
                  <tr>
                    <th className={styles.tableHeaderCell}>Rarity</th>
                    <th className={styles.tableHeaderCell}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {setEntry.rarities.map((r, idx) => (
                    <tr key={r.rarity} className={styles.tableRow}>
                      <td className={styles.tableCellBold}>{r.rarity}</td>
                      <td className={styles.tableCell}>
                        <input
                          type="number"
                          min={0}
                          className={styles.tableInput}
                          value={r.count}
                          onChange={e => updateRarityCount(setEntry.id, idx, e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {/* Add Another Set button */}
      <div className={styles.formSection}>
        <button
          onClick={addAnotherSet}
          className={styles.createGroupButton}
          style={{ width: '100%' }}
        >
          + Select Another Set
        </button>
      </div>

      {/* Submit */}
      <button
        disabled={selectedSets.some(entry => !entry.selectedSetId)}
        onClick={createSession}
        className={styles.submitButton}
      >
        Create Session{selectedSets.length > 1 ? 's' : ''}
      </button>
    </div>
  );
}
